/**
 * Rynora Agent — autonomous QA teammate (`pending-rynora-agent`).
 *
 * Contracts for the v0 surface — agent findings registry, draft-flow
 * payload, daily-digest shape, and the audit-log identity. v1 will
 * extend this module with per-org persona / budget rows; v2 with the
 * autonomous fix-PR loop.
 *
 * Design doc: docs/handbook/features/pending/rynora-agent.md
 * Strategy:   docs/handbook/market/2026-q3-ai-era-strategy.md (§2.1)
 */

import { z } from 'zod';

// ---- Finding kinds ----

/**
 * The discovery source the agent used to surface a finding.
 *
 * v0 only writes `'uncovered_page'`. The other kinds reserve slots in
 * the CHECK constraint (migration 114) so the v1 prod-feedback loop
 * (`pending-prod-feedback-loop`), flake-spike detector, perf-budget
 * watcher, and a11y-regression checker can land without a schema
 * change — they just flip the `kind` enum to one of the reserved
 * values.
 */
export const AgentFindingKindSchema = z.enum([
  'uncovered_page',
  'prod_error',
  'flake_spike',
  'perf_breach',
  'a11y_regression',
  /**
   * `pending-ai-exploratory-tester` — Session 1 (2026-05-30) closes the
   * contracts-behind-DB gap: migration 121 (2026-05-29) already widened
   * `agent_findings.kind` to accept this value, but the contracts enum
   * lagged. Session 1 brings the contracts forward + adds the payload
   * variant (`ExploratoryFindingPayloadSchema`) + admin-config knobs
   * (`AgentExploratoryConfigSchema`). The runner-side random-action
   * script that actually surfaces findings lands in Session 2.
   */
  'exploratory_finding',
]);
export type AgentFindingKind = z.infer<typeof AgentFindingKindSchema>;

// ---- Finding lifecycle ----

/**
 * State machine:
 *
 *   open       → agent surfaced the finding; no draft yet (e.g. we
 *                hit `maxDraftsPerDay` and shelved it for tomorrow).
 *   drafted    → agent created a stub test case; awaiting human review.
 *   approved   → human accepted; the stub is now a normal flow.
 *   filed      → v1+ — agent filed a Linear/Jira ticket linking the
 *                draft. v0 leaves this unused.
 *   fixed      → v2 — the agent's proposed fix-PR has merged.
 *   dismissed  → human rejected the finding (and optionally soft-
 *                deleted the drafted stub).
 *
 * The legal transitions are enforced in `agent-service.ts`; the DB
 * CHECK enforces only the value set.
 */
export const AgentFindingStatusSchema = z.enum(['open', 'drafted', 'approved', 'filed', 'fixed', 'dismissed']);
export type AgentFindingStatus = z.infer<typeof AgentFindingStatusSchema>;

export const AgentHumanDecisionSchema = z.enum(['approve', 'dismiss']);
export type AgentHumanDecision = z.infer<typeof AgentHumanDecisionSchema>;

// ---- Per-kind payload ----

/**
 * `kind: 'uncovered_page'` payload. The agent records the URL that
 * has no flow whose first navigate step targets the same path. v0
 * compares paths only (host + query + fragment ignored) per the
 * locked decision #3 in the design doc.
 */
export const UncoveredPagePayloadSchema = z.object({
  url: z.string().min(1),
  path: z.string().min(1),
});
export type UncoveredPagePayload = z.infer<typeof UncoveredPagePayloadSchema>;

/**
 * `kind: 'prod_error'` payload. Written by
 * `pending-prod-feedback-loop` v0 — Sentry ingest worker. Stores the
 * minimum context needed to render the digest line + deeplink back
 * to the upstream provider. The full event body is NOT stored — only
 * what fits in the row.
 *
 * `source` is the external provider (`'sentry'` in v0). `externalId`
 * is the provider-native issue identifier (`Sentry.Issue.id`) and is
 * the basis of the `dedup_key` (`prod_error:<source>:<externalId>`).
 */
export const ProdErrorPayloadSchema = z.object({
  source: z.enum(['sentry', 'datadog', 'rollbar', 'bugsnag']),
  externalId: z.string().min(1),
  /** Human title of the issue ("TypeError: cannot read 'foo' of undefined"). */
  title: z.string().min(1),
  /** Sentry's `culprit` or equivalent — usually the throwing function name + file. */
  culprit: z.string().default(''),
  /** URL the last event was captured on, if any. Used by v0.1 to seed the navigate step. */
  lastEventUrl: z.string().default(''),
  /** Provider deeplink for the issue (Sentry's `permalink`). */
  permalink: z.string().default(''),
  /** Provider severity tag (`error` / `warning` / `info` / `fatal`). */
  level: z.string().default('error'),
  /** Distinct user count Sentry has seen for this issue. */
  userCount: z.number().int().nonnegative().default(0),
  /** Total event count Sentry has seen for this issue. */
  eventCount: z.number().int().nonnegative().default(0),
  /** ISO-8601 timestamp the provider last saw the issue. */
  lastSeen: z.string().default(''),
  /**
   * How many times this finding has been touched by the ingest
   * worker. Starts at 1 on insert; incremented on every conflict-
   * path upsert. Used to detect "noisy recurring" issues in the
   * digest without re-querying Sentry.
   */
  occurrenceCount: z.number().int().positive().default(1),
});
export type ProdErrorPayload = z.infer<typeof ProdErrorPayloadSchema>;

/**
 * `kind: 'exploratory_finding'` payload — written by the
 * `pending-ai-exploratory-tester` pass.
 *
 * The 5 finding sub-kinds locked in the design doc are split out into
 * `ExploratoryFindingSubKindSchema` so a single row's `subKind` cell
 * is type-safe even though the parent `kind` column is just
 * `'exploratory_finding'`. Dedup at (url, subKind, elementSelector)
 * granularity — locked design-doc decision #4.
 *
 * Session 1 (this contracts surface) — payload + admin-config knobs.
 * Session 2 — runner-side random-action driver writes rows with the
 * concrete `subKind` set.
 */
export const ExploratoryFindingSubKindSchema = z.enum([
  /** HTTP 5xx response captured while exercising the page. */
  'http_5xx',
  /** `console.error` line emitted while exercising the page. */
  'console_error',
  /** axe-core rule violation at the configured `failOnSeverity` or above. */
  'axe_violation',
  /** Modal/dialog failed to trap focus past its last interactive element. */
  'focus_trap_escape',
  /** Browser back-button re-POSTed or otherwise broke the navigation history. */
  'broken_back_button',
]);
export type ExploratoryFindingSubKind = z.infer<typeof ExploratoryFindingSubKindSchema>;

export const ExploratoryFindingPayloadSchema = z.object({
  /** One of the 5 locked sub-kinds. Drives `composeDailyDigest` row copy. */
  subKind: ExploratoryFindingSubKindSchema,
  /** Page URL where the finding was observed. */
  url: z.string().min(1),
  /**
   * What the exploratory pass observed — e.g.
   * `"console.error: undefined is not a function"`. Truncated at 200
   * chars when written (locked design-doc decision #8). Plain text;
   * never an HTML fragment.
   */
  observation: z.string().min(1),
  /**
   * Optional CSS-selector-ish path to the element that triggered the
   * finding. Hashed in the `dedup_key` so the same broken button on
   * the same page never writes N rows.
   */
  elementSelector: z.string().nullable().default(null),
  /**
   * How many times this finding has been touched by the exploratory
   * pass. Starts at 1 on insert; incremented on every conflict-path
   * upsert. Used to detect "noisy recurring" findings in the digest.
   */
  occurrenceCount: z.number().int().positive().default(1),
  /** ISO-8601 timestamp the finding was first observed. Pinned at insert. */
  firstSeen: z.string().default(''),
});
export type ExploratoryFindingPayload = z.infer<typeof ExploratoryFindingPayloadSchema>;

/**
 * Discriminated union of all per-kind payloads. v0 ships
 * `uncovered_page` (rynora-agent) and `prod_error` (prod-feedback-loop
 * Sentry connector); Session 1 of `pending-ai-exploratory-tester`
 * adds `exploratory_finding`. The other variants land with their
 * respective workers in v1+.
 */
export const AgentFindingPayloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('uncovered_page'), data: UncoveredPagePayloadSchema }),
  z.object({ kind: z.literal('prod_error'), data: ProdErrorPayloadSchema }),
  z.object({ kind: z.literal('exploratory_finding'), data: ExploratoryFindingPayloadSchema }),
]);
export type AgentFindingPayload = z.infer<typeof AgentFindingPayloadSchema>;

// ---- Finding row ----

/**
 * Wire shape of one row in the `agent_findings` table.
 */
export const AgentFindingSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  kind: AgentFindingKindSchema,
  source: z.string().min(1),
  dedupKey: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  status: AgentFindingStatusSchema,
  draftedTestCaseId: z.string().uuid().nullable(),
  humanDecision: AgentHumanDecisionSchema.nullable(),
  decidedAt: z.string().nullable(),
  decidedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentFinding = z.infer<typeof AgentFindingSchema>;

// ---- Daily digest ----

/**
 * Composed payload for the daily Slack / email / in-app digest. v0
 * lists newly-drafted findings + a backlog count; v1 will compose
 * additional sections (flake spikes, perf breaches, a11y regressions)
 * as new discovery sources come online.
 */
export const AgentDailyDigestSchema = z.object({
  orgId: z.string().uuid(),
  generatedAt: z.string(),
  windowHours: z.number().int().positive(),
  draftedFindings: z.array(
    z.object({
      id: z.string().uuid(),
      kind: AgentFindingKindSchema,
      summary: z.string().min(1),
      deeplink: z.string().min(1),
    }),
  ),
  openBacklogCount: z.number().int().nonnegative(),
});
export type AgentDailyDigest = z.infer<typeof AgentDailyDigestSchema>;

// ---- Per-org overrides (pending-rynora-agent-v1 Session 1 / migration 127) ----

/**
 * Raw override row from `agent_org_config`. Every field is nullable
 * because NULL = "inherit admin-config". A missing row is equivalent
 * to an all-NULL row; the service-layer `getEffectiveAgentConfig`
 * helper handles both cases uniformly.
 *
 * Why nullable instead of a defaulted shape on the API surface: the
 * Project Settings → Agent UI needs to distinguish "this org has
 * explicitly pinned `runHourUtc=7`" from "this org has not opted in
 * to overrides yet" — without nullability the UI can't show the
 * "inherit" affordance.
 */
export const AgentOrgConfigOverrideSchema = z.object({
  enabled: z.boolean().nullable(),
  runHourUtc: z.number().int().min(0).max(23).nullable(),
  maxDraftsPerDay: z.number().int().nonnegative().max(100).nullable(),
  crawlerEnabled: z.boolean().nullable(),
  /**
   * `pending-ai-exploratory-tester` Session 1 — per-org exploratory
   * pass kill switch. NULL → inherit admin-config
   * (`agent.exploratory.enabled`); FALSE pins the pass OFF for this
   * org regardless of admin default. Three-layer kill switch matches
   * the locked design-doc decision #11.
   */
  exploratoryEnabled: z.boolean().nullable().default(null),
  /**
   * Per-org wall-clock budget override (minutes). NULL → inherit
   * admin-config (default 30). Range 5–120 to match locked
   * design-doc decision #2.
   */
  exploratoryWallClockMinutes: z.number().int().min(5).max(120).nullable().default(null),
  /**
   * Per-org daily findings cap. NULL → inherit admin-config (default
   * 20). Caps the dedup-respected row writes per (org, project, day)
   * so a noisy site can't blow up `agent_findings`.
   */
  exploratoryMaxFindingsPerDay: z.number().int().nonnegative().max(500).nullable().default(null),
  /**
   * Per-org Slack default channel id (`C…` / `G…`). NULL → digest
   * may inherit `admin_config.agent.slack.defaultChannel`, but Slack
   * `/approve` org binding (Phase 8.21.9) **requires** a non-NULL
   * per-org pin so channel → org is unique and fail-closed.
   */
  slackDefaultChannel: z.string().nullable().default(null),
});
export type AgentOrgConfigOverride = z.infer<typeof AgentOrgConfigOverrideSchema>;

/**
 * Effective per-org config — what the worker actually uses for a
 * given org. Every field has a concrete value because the merge
 * resolves NULL cells to the admin-config default. Surfaced via
 * `GET /api/orgs/:orgId/agent-config` so the Project Settings UI can
 * preview the merged result alongside the raw override.
 *
 * `runHourUtc` is the per-org schedule anchor — when the worker
 * wakes hourly, it picks up an org only when current UTC hour ===
 * the effective value. `lastTickAt` carries the restart-safe
 * 23-hour floor that prevents a redeploy from re-ticking the org.
 */
export const AgentOrgConfigEffectiveSchema = z.object({
  orgId: z.string().uuid(),
  enabled: z.boolean(),
  runHourUtc: z.number().int().min(0).max(23),
  maxDraftsPerDay: z.number().int().nonnegative().max(100),
  crawlerEnabled: z.boolean(),
  lastTickAt: z.string().nullable(),
  /**
   * Effective exploratory-pass shape after merging admin-config and
   * per-org override. All three fields are concrete (the merge
   * resolves NULL cells to admin defaults). `lastExploratoryTickAt`
   * is the restart-safe 23-hour floor anchor for the exploratory
   * pass — distinct from the agent-tick `lastTickAt` so the two
   * cadences are independent.
   */
  exploratoryEnabled: z.boolean(),
  exploratoryRunHourUtc: z.number().int().min(0).max(23),
  exploratoryWallClockMinutes: z.number().int().min(5).max(120),
  exploratoryMaxFindingsPerDay: z.number().int().nonnegative().max(500),
  lastExploratoryTickAt: z.string().nullable(),
});
export type AgentOrgConfigEffective = z.infer<typeof AgentOrgConfigEffectiveSchema>;

/**
 * Composite returned by `GET /api/orgs/:orgId/agent-config`. The UI
 * binds inputs to `override` (so cleared cells write NULL) and shows
 * `effective` as the resolved preview underneath.
 */
export const AgentOrgConfigResponseSchema = z.object({
  effective: AgentOrgConfigEffectiveSchema,
  override: AgentOrgConfigOverrideSchema,
});
export type AgentOrgConfigResponse = z.infer<typeof AgentOrgConfigResponseSchema>;

/**
 * Body for `PUT /api/orgs/:orgId/agent-config`. Mirrors the override
 * schema — every cell is independently nullable so a single PUT can
 * pin one field while clearing another.
 */
export const AgentOrgConfigUpsertBodySchema = AgentOrgConfigOverrideSchema;
export type AgentOrgConfigUpsertBody = z.infer<typeof AgentOrgConfigUpsertBodySchema>;

// ---- Exploratory manual-trigger route ----

/**
 * `pending-ai-exploratory-tester` Session 1 — wire shape for
 * `POST /api/agent/exploratory/run-once?projectId=…`. The route is
 * the operator-initiated bypass of the daily exploratory schedule;
 * Session 1 ships it in stub mode (`findingsRecorded: 0` because the
 * runner is stubbed). Session 2 makes the body do real work without
 * a shape change. The response carries enough context for the UI to
 * render a per-finding-count toast + a "queued for tomorrow" hint
 * when budget didn't admit every URL.
 *
 * `skippedReason` is set ONLY when the pass short-circuited. For
 * `force=true` (the route always passes force) the only reachable
 * value is `'disabled'` — the route returns 409 Conflict in that
 * case so the UI can distinguish "you have to enable the pass
 * first" from a successful no-op.
 */
export const ExploratoryRunOnceResponseSchema = z.object({
  ranOnce: z.boolean(),
  urlsExplored: z.number().int().nonnegative(),
  findingsRecorded: z.number().int().nonnegative(),
  urlsDeferred: z.number().int().nonnegative(),
  skippedReason: z.enum(['disabled', 'wrong_hour', 'tick_floor']).optional(),
});
export type ExploratoryRunOnceResponse = z.infer<typeof ExploratoryRunOnceResponseSchema>;

// ---- Audit constants ----

/**
 * Audit-log action codes the agent + prod-feedback-loop write.
 * Mirrors migration 114's `valid_action` additions, plus migration
 * 116's addition of `AGENT_FINDING_DRAFT_FAILED`
 * (`pending-prod-feedback-loop-v01`).
 */
export const AGENT_AUDIT_ACTIONS = [
  'AGENT_FINDING_CREATED',
  'AGENT_FINDING_APPROVED',
  'AGENT_FINDING_DISMISSED',
  /**
   * `pending-prod-feedback-loop-v01` — the ingest worker captured a
   * Sentry finding (a prior `AGENT_FINDING_CREATED` row exists) but
   * the LLM call to draft a reproduction flow failed. The finding
   * stays at `status='open'` so the next ingest tick can retry.
   * The `meta` payload carries `{ reason: string, warnings: string[] }`.
   */
  'AGENT_FINDING_DRAFT_FAILED',
  /**
   * `pending-rynora-agent-v1` Session 1 — an org admin / owner
   * upserted the `agent_org_config` row via
   * `PUT /api/orgs/:orgId/agent-config`. Meta carries the
   * pre-image and post-image of the override row so the change is
   * fully reconstructible from audit alone.
   */
  'AGENT_ORG_CONFIG_UPDATED',
  /**
   * `pending-rynora-agent-v1` Session 2 — the Slack `/approve`
   * receiver rejected a slash-command attempt AFTER signature
   * verification succeeded. The success path uses the existing
   * `AGENT_FINDING_APPROVED` verb (preserves v0's audit shape for
   * approvals regardless of which surface produced them); this
   * failure verb is dedicated so an operator can dashboard
   * "Slack-driven approval failures" without confusing them with
   * the success rows.
   *
   * `meta`:
   *   - `reason`: `'unknown_finding'` / `'cross_org'` / `'not_drafted'` / `'malformed_text'`
   *   - `slackUserId` / `slackTeamId` / `slackChannel`
   *   - `findingId` (if parsable)
   *
   * Note: pre-signature-verification rejects (bad HMAC, expired
   * timestamp, missing headers) do NOT write an audit row because
   * the request is unauthenticated by definition — we have no
   * trustworthy `orgId` to bind the row to. Those land as
   * structured-log lines only.
   */
  'AGENT_SLACK_APPROVE_FAILED',
  /**
   * `pending-ai-exploratory-tester` Session 1 — an owner / admin
   * triggered the exploratory pass via
   * `POST /api/agent/exploratory/run-once`. The verb is distinct
   * from `AGENT_FINDING_CREATED` so audit dashboards split out
   * exploratory traffic (often noisy) without confusing it with
   * the regular agent / Sentry / Slack-driven findings.
   *
   * `meta`:
   *   - `projectId` (the run is project-scoped)
   *   - `triggeredBy` (user id of the actor)
   *   - `urlsExplored` (count of crawl_root_urls the pass walked)
   *   - `findingsRecorded` (count of `agent_findings` rows written;
   *     0 in Session 1 because the runner is still stubbed; Session
   *     2 fills this in)
   *   - `via`: `'manual'` (always — the daily worker tick does NOT
   *     write this verb, only the manual-trigger HTTP route does;
   *     keeps the verb meaningful as "an operator pressed the
   *     button" rather than overloading it with scheduled traffic)
   */
  'AGENT_EXPLORATORY_TRIGGERED',
] as const;
export type AgentAuditAction = (typeof AGENT_AUDIT_ACTIONS)[number];

/**
 * Convention for the lazily-created per-org synthetic user. Stored
 * in the existing `users` table; the slug is the `organizations.slug`
 * (lowercased, kebab-cased). This is the only canonical place the
 * email shape lives — agent-service.ts builds the email by calling
 * `agentUserEmail(slug)` so the format never drifts between writer
 * and audit-search reader.
 */
export function agentUserEmail(orgSlug: string): string {
  return `agent@${orgSlug}.rynora.local`;
}
